/*
  Science page — ingredient drawers.

  Any element with [data-sci-drawer-open="<id>"] (or a link to "#<id>") opens
  <dialog data-sci-drawer="<id>">, so triggers can live in any section on the page.
  Drawers can also be deep-linked from emails/ads with a URL hash, e.g. /pages/science#ingredient-lions-mane
*/
(() => {
  if (window.sciDrawers) return;

  const LOCK_CLASS = 'sci-scroll-lock';

  // Resolve a drawer for a trigger. A trigger tied to one specific drawer (a stack
  // row, via data-sci-drawer-uid) wins over the shared key; either way the trigger's
  // own section is searched first so duplicated sections open their own drawers.
  const find = (id, trigger) => {
    const uid = trigger && trigger.getAttribute && trigger.getAttribute('data-sci-drawer-uid');
    const section = trigger && trigger.closest && trigger.closest('.shopify-section');
    const selectors = [];
    if (uid) selectors.push(`dialog[data-sci-drawer-uid="${CSS.escape(uid)}"]`);
    if (id) selectors.push(`dialog[data-sci-drawer="${CSS.escape(id)}"]`);
    for (const selector of selectors) {
      const match = (section && section.querySelector(selector)) || document.querySelector(selector);
      if (match) return match;
    }
    return null;
  };

  const unlockIfIdle = () => {
    if (!document.querySelector('dialog.sci-drawer[open]')) {
      document.documentElement.classList.remove(LOCK_CLASS);
    }
  };

  const open = (id, trigger) => {
    const dialog = find(id, trigger);
    if (!dialog) return false;
    if (dialog.open) return true;

    document.querySelectorAll('dialog.sci-drawer[open]').forEach(close);

    dialog.showModal();
    document.documentElement.classList.add(LOCK_CLASS);
    const scroller = dialog.querySelector('.sci-drawer__scroll');
    if (scroller) scroller.scrollTop = 0;
    return true;
  };

  const close = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  const closeAll = () => document.querySelectorAll('dialog.sci-drawer[open]').forEach((d) => close(d));

  const idFromHref = (href) => {
    if (!href || href.charAt(0) !== '#' || href.length < 2) return null;
    try {
      return decodeURIComponent(href.slice(1));
    } catch (e) {
      return href.slice(1);
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sci-drawer-open]');
    if (trigger) {
      if (open(trigger.getAttribute('data-sci-drawer-open'), trigger)) event.preventDefault();
      return;
    }

    const link = event.target.closest('a[href^="#"]');
    if (link && find(idFromHref(link.getAttribute('href')), link)) {
      event.preventDefault();
      open(idFromHref(link.getAttribute('href')), link);
      return;
    }

    const closer = event.target.closest('[data-sci-drawer-close]');
    if (closer) {
      close(closer.closest('dialog'));
      return;
    }

    // Clicks on the dialog itself (outside the panel) are backdrop clicks.
    if (event.target.matches && event.target.matches('dialog.sci-drawer')) {
      close(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches && event.target.matches('dialog.sci-drawer')) {
        unlockIfIdle();
      }
    },
    true
  );

  const openFromHash = () => {
    const id = idFromHref(window.location.hash);
    if (find(id)) open(id);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHash);
  } else {
    openFromHash();
  }
  window.addEventListener('hashchange', openFromHash);

  // Theme editor: selecting an ingredient block opens its drawer so it can be edited in place.
  document.addEventListener('shopify:block:select', (event) => {
    const trigger = event.target.querySelector?.('[data-sci-drawer-open]') || event.target.closest?.('[data-sci-drawer-open]');
    if (trigger && trigger.closest('[data-sci-ingredients]')) open(trigger.getAttribute('data-sci-drawer-open'), trigger);
  });
  document.addEventListener('shopify:block:deselect', closeAll);
  document.addEventListener('shopify:section:unload', () => {
    document.querySelectorAll('dialog.sci-drawer[open]').forEach(close);
    unlockIfIdle();
  });

  window.sciDrawers = { open, close, closeAll };
})();
